import { useEffect } from 'react';

// Extracted from the four identical polling effects in Discover.js, Region.js,
// Aircraft.js, and RapSheet.js (see docs/superpowers/specs/2026-07-18-execution-layer-redesign-plan.md).
export const LIVE_POLL_MS = 25_000;

/**
 * Calls `fn` immediately, then on a visibility-aware interval, re-polling
 * whenever the tab regains visibility. Cleans up its interval and listener
 * on unmount.
 *
 * `fn` receives an `isStale` predicate that turns true once this effect
 * instance is torn down (unmount, or `fn`/`ms` identity change). Async
 * callers must check it before committing state, or an in-flight response
 * for the previous params can land after navigation and overwrite fresh
 * data — the original per-surface effects guarded this with a `cancelled`
 * closure flag.
 * @param {(isStale: () => boolean) => void} fn
 * @param {number} [ms]
 */
export function usePoll(fn, ms = LIVE_POLL_MS) {
  useEffect(() => {
    let stale = false;
    const isStale = () => stale;
    fn(isStale);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') fn(isStale);
    }, ms);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fn(isStale);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stale = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, ms]);
}
